import { React, useState, useCallback, useEffect, useMemo } from 'react';
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { motion } from 'framer-motion';
import "./index.scss";

import SideNav from "../../components/PostLogin/SideNav";
import UsersTileView from "../../components/PostLogin/Users/UsersTileView";
import UserListView from "../../components/PostLogin/Users/UsersListView";

import Pagination from '../../components/ui/Pagination';
import NoDataFound from "../../components/ui/NoDataFound";
import SideModal from "../../components/ui/SideModal";
import { toaster } from "../../components/ui/Toaster";
import SkeletonLoader from "../../components/ui/SkeletonLoader";

import InviteUserModal from "../../components/PostLogin/Users/InviteUserModal";
import InvitedUserListModal from "../../components/PostLogin/Users/InvitedUsersListModal";

import { tileViewIcon, listViewIcon, searchIcon } from "../../utils/icons.js";
import { formatNumber } from "../../utils/util";

import { useSelector, useDispatch } from 'react-redux';
import { getUsersList } from '../../store/actions/organizationActions';
import { resetUpdateUserStatus, getUserStats, resetInviteUserResponse } from "../../store/actions/usersAction";

const Users = () => {

    const userStatsResponse = useSelector((state) => state.users?.userStatsResponse);
    const usersList = useSelector((state) => state.organization?.usersList);
    const updateUserStatusSingleResponse = useSelector((state) => state.users?.updateUserStatusSingleResponse);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const [viewType, setViewType] = useState('tile');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [updatedSearchValue, setUpdatedSearchValue] = useState("");
    const [showInviteUserModal, setShowInviteUserModal] = useState(false);
    const [showInvitedUserListModal, setShowInvitedUserListModal] = useState(false);
    const [isStatsLoading, setIsStatsLoading] = useState(false);


    const closeInviteUserModal = () => {
        dispatch(resetInviteUserResponse())
        setShowInviteUserModal(false)
    }


    const searchUsers = (event) => {
        setSearchValue(event.target.value);
        requestSearch(event.target.value)
        setCurrentPage(1);       
    }

    const requestSearch = useMemo(() => debounce((searchTerm) => {
        setUpdatedSearchValue(searchTerm)
    }, 500), []);

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const fetchUser = useCallback(()=>{        
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}`;
        const queryParams = updatedSearchValue ? `&query=${updatedSearchValue}` : "";
        const url = `${baseUrl}${queryParams}`;
        setIsLoading(true)
        dispatch(getUsersList(url));
    },[currentPage, currentPageSize, updatedSearchValue, dispatch])

    const fetchUserStats = useCallback(()=>{
        setIsStatsLoading(true)
        dispatch(getUserStats())
    },[dispatch])

    useEffect(()=>{
        fetchUserStats()
    },[fetchUserStats])
    
    useEffect(()=>{
        fetchUser();
    },[fetchUser, fetchUserStats])

    useEffect(()=>{
        if(usersList !== null){
            setIsLoading(false)
        }
    },[usersList])

    useEffect(()=>{
        if(updateUserStatusSingleResponse != null) {
            if(updateUserStatusSingleResponse?.detail?.status === "success") {
                toaster.addToast(updateUserStatusSingleResponse?.detail?.description, "success");
                fetchUser();
                fetchUserStats();
            } else {
                toaster.addToast(updateUserStatusSingleResponse?.description, "error");
            }
            dispatch(resetUpdateUserStatus())
        }
    },[updateUserStatusSingleResponse, dispatch, fetchUser, fetchUserStats])

    useEffect(()=>{
        if(userStatsResponse != null) {
            setIsStatsLoading(false);
        }
    },[userStatsResponse])

    return (
        <div className="users-container">
            <SideNav />
            <div className="users-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left">
                        <h1>{t("users.heading")}</h1>
                    </div>
                    <div className="header-right d-flex gap-3">
                        <button className="button invite-users" onClick={()=>{setShowInviteUserModal(true)}}><span>&#43;</span>{t("users.inviteUsers")}</button>
                        <button className="button invited-users" onClick={()=>{setShowInvitedUserListModal(true)}}>Invited Users</button>
                    </div>
                </div>
                <div className="page-search-header px-4 py-3  d-flex align-items-center justify-content-between">
                    <div className="left-search-container position-relative">
                        <span className="position-absolute search-icon">{searchIcon()}</span>
                        <input type='text' placeholder='Search User' className="search-input" value={searchValue} onChange={searchUsers}/>
                    </div>
                    <div className="right-view-option d-flex">
                        <div className={`tile-view-image ${viewType === 'tile' ? 'active' : ''}`} onClick={()=>setViewType('tile')}>
                            {tileViewIcon()}
                        </div>
                        <div className={`list-view-image ${viewType === 'list' ? 'active' : ''}`} onClick={()=>setViewType('list')}>
                            {listViewIcon()}
                        </div>
                    </div>
                </div>
                <div className="page-main-section p-4">
                    <div className="users-header-container p-3 mt-3 rounded">
                        <div className="d-flex gap-3">
                            <div className="total-score-container rounded">
                                {
                                    isStatsLoading ?
                                    <SkeletonLoader type={"line"} count={2} currentPage={"users"}/>
                                    :
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div className="users-totol-count d-flex">
                                            <p className="total-user-count">{formatNumber(userStatsResponse?.users?.total)}</p>
                                            <div className="user-count mx-3 mt-2">
                                                <span className="active d-inline-block mx-1"></span>
                                                <span className="disbled d-inline-block"></span>
                                                <p>{t("users.totalUsers")}</p>
                                            </div>
                                        </div>
                                        <div className="active-disabled-count d-flex gap-3">
                                            <p className="active-user"><span className="d-inline-block">{formatNumber(userStatsResponse?.users?.active)}</span> {t("users.activeUsers")}</p>
                                            <p className="disabled-user"><span className="d-inline-block">{formatNumber(userStatsResponse?.users?.disabled)}</span> {t("users.disabledUsers")}</p>
                                        </div>
                                    </motion.div>
                                }
                            </div>
                        </div>
                    </div>
                    <h2 className="secondary-heading m-0">{t("users.selectaUser")}</h2>
                    {viewType === "tile" && <UsersTileView usersList={usersList} loading={isLoading}/>}
                    {viewType === "list" && <UserListView usersList={usersList} loading={isLoading} />}
                    {usersList != null && !isLoading && usersList?.meta?.total_results !== 0 && <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        totalCount={usersList?.meta?.total_results}
                        onPageChange={setCurrentPageandRecordSize}
                    />}
                    {
                        !isLoading && usersList?.meta?.total_results === 0 && 
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <NoDataFound />
                        </motion.div>
                    }
                </div>
                <SideModal showSidePanel={showInviteUserModal} hideSidePanel={closeInviteUserModal}>
                    {showInviteUserModal && <InviteUserModal closeInviteUser={closeInviteUserModal}/>}
                </SideModal>
                <SideModal showSidePanel={showInvitedUserListModal} hideSidePanel={()=>setShowInvitedUserListModal(false)}>
                    {showInvitedUserListModal && <InvitedUserListModal />}
                </SideModal>
            </div>
        </div>
    )
}

export default Users;